import {
  AvailabilityHistoryEntry,
  ImportedListing,
  PriceHistoryEntry,
} from '../_types/imported-listing'
import { ONE_DAY_MS, ONE_WEEK_MS } from '../_utilities/relative-time'

/**
 * Builds a representative set of imported listings for the dev/local
 * fallback. Each listing has a price + availability history that lets the
 * detail-page sparkline + timeline render meaningfully. Listing ids match
 * the ones the notification seed references so click-throughs actually
 * land on real records.
 *
 * @param {string} quickPassUserId - the QuickPass user the seed is scoped to
 * @returns {ImportedListing[]} a deterministic seed list, newest first
 */
const buildSeedImportedListings = (
  quickPassUserId: string,
): ImportedListing[] => {
  const now = Date.now()

  const libertyVillagePriceHistory: PriceHistoryEntry[] = [
    {
      observedAt: now - 5 * ONE_DAY_MS,
      priceMonthly: 2495,
      priceCurrency: 'CAD',
    },
    {
      observedAt: now - 4 * ONE_DAY_MS,
      priceMonthly: 2495,
      priceCurrency: 'CAD',
    },
    {
      observedAt: now - 3 * ONE_DAY_MS,
      priceMonthly: 2495,
      priceCurrency: 'CAD',
    },
    {
      observedAt: now - 2 * ONE_DAY_MS,
      priceMonthly: 2450,
      priceCurrency: 'CAD',
    },
    {
      observedAt: now - 1 * ONE_DAY_MS,
      priceMonthly: 2450,
      priceCurrency: 'CAD',
    },
    {
      observedAt: now - 4 * 60 * 60 * 1000,
      priceMonthly: 2395,
      priceCurrency: 'CAD',
    },
  ]

  const libertyVillageAvailability: AvailabilityHistoryEntry[] = [
    {
      observedAt: now - 5 * ONE_DAY_MS,
      isAvailable: true,
      availableDate: now + 30 * ONE_DAY_MS,
    },
    {
      observedAt: now - 1 * ONE_DAY_MS,
      isAvailable: true,
      availableDate: now + 30 * ONE_DAY_MS,
    },
  ]

  const junctionLoftPriceHistory: PriceHistoryEntry[] = [
    {
      observedAt: now - 14 * ONE_DAY_MS,
      priceMonthly: 2150,
      priceCurrency: 'CAD',
    },
    {
      observedAt: now - 10 * ONE_DAY_MS,
      priceMonthly: 2150,
      priceCurrency: 'CAD',
    },
    {
      observedAt: now - 7 * ONE_DAY_MS,
      priceMonthly: 2150,
      priceCurrency: 'CAD',
    },
    {
      observedAt: now - 1 * ONE_DAY_MS,
      priceMonthly: 2150,
      priceCurrency: 'CAD',
    },
    {
      observedAt: now - 9 * 60 * 60 * 1000,
      priceMonthly: 2150,
      priceCurrency: 'CAD',
    },
  ]

  const junctionLoftAvailability: AvailabilityHistoryEntry[] = [
    {
      observedAt: now - 14 * ONE_DAY_MS,
      isAvailable: true,
      availableDate: null,
    },
    {
      observedAt: now - 7 * ONE_DAY_MS,
      isAvailable: false,
      availableDate: null,
    },
    {
      observedAt: now - 9 * 60 * 60 * 1000,
      isAvailable: true,
      availableDate: null,
    },
  ]

  const bloorBungalowPriceHistory: PriceHistoryEntry[] = [
    {
      observedAt: now - 3 * ONE_WEEK_MS,
      priceMonthly: 2800,
      priceCurrency: 'CAD',
    },
    {
      observedAt: now - 2 * ONE_WEEK_MS,
      priceMonthly: 2800,
      priceCurrency: 'CAD',
    },
    {
      observedAt: now - 1 * ONE_WEEK_MS,
      priceMonthly: 2800,
      priceCurrency: 'CAD',
    },
    {
      observedAt: now - 2 * ONE_DAY_MS,
      priceMonthly: 2800,
      priceCurrency: 'CAD',
    },
    {
      observedAt: now - 1 * ONE_DAY_MS,
      priceMonthly: 2950,
      priceCurrency: 'CAD',
    },
  ]

  const bloorBungalowAvailability: AvailabilityHistoryEntry[] = [
    {
      observedAt: now - 3 * ONE_WEEK_MS,
      isAvailable: true,
      availableDate: now + 14 * ONE_DAY_MS,
    },
  ]

  const roncesvallesCondoPriceHistory: PriceHistoryEntry[] = [
    {
      observedAt: now - 4 * ONE_WEEK_MS,
      priceMonthly: 2300,
      priceCurrency: 'CAD',
    },
    {
      observedAt: now - 3 * ONE_WEEK_MS,
      priceMonthly: 2300,
      priceCurrency: 'CAD',
    },
    {
      observedAt: now - 2 * ONE_WEEK_MS,
      priceMonthly: 2300,
      priceCurrency: 'CAD',
    },
    {
      observedAt: now - 1 * ONE_WEEK_MS,
      priceMonthly: 2300,
      priceCurrency: 'CAD',
    },
    {
      observedAt: now - 3 * ONE_DAY_MS,
      priceMonthly: 2300,
      priceCurrency: 'CAD',
    },
    {
      observedAt: now - 2 * ONE_DAY_MS,
      priceMonthly: 2300,
      priceCurrency: 'CAD',
    },
  ]

  const roncesvallesCondoAvailability: AvailabilityHistoryEntry[] = [
    {
      observedAt: now - 4 * ONE_WEEK_MS,
      isAvailable: true,
      availableDate: null,
    },
    {
      observedAt: now - 2 * ONE_DAY_MS,
      isAvailable: false,
      availableDate: null,
    },
  ]

  return [
    {
      id: 'imported_seed_liberty_two_bed',
      quickPassUserId,
      sourceUrl: 'https://rentals.ca/toronto/liberty-village-two-bed-12345',
      sourceSite: 'rentals.ca',
      title: 'Bright two-bedroom in Liberty Village',
      description:
        'Open-concept two-bed two-bath with floor-to-ceiling windows, full-size in-suite laundry, and a south-facing balcony. Steps from King West, Stackt Market, and the GO. Building has gym, party room, and 24h concierge.',
      imageUrls: [
        'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=900',
        'https://images.unsplash.com/photo-1493809842364-78817add7ffb?w=900',
        'https://images.unsplash.com/photo-1493663284031-b7e3aefcae8e?w=900',
      ],
      priceMonthly: 2395,
      priceCurrency: 'CAD',
      bedrooms: 2,
      bathrooms: 2,
      propertyType: 'apartment',
      city: 'Toronto',
      fullAddress: '85 East Liberty St, Toronto, ON',
      isAvailable: true,
      availableDate: now + 30 * ONE_DAY_MS,
      lastScrapedAt: now - 4 * 60 * 60 * 1000,
      scrapeStatus: 'success',
      scrapeNotes: '',
      priceHistory: libertyVillagePriceHistory,
      availabilityHistory: libertyVillageAvailability,
      createdAt: now - 5 * ONE_DAY_MS,
      updatedAt: now - 4 * 60 * 60 * 1000,
    },
    {
      id: 'imported_seed_junction_loft',
      quickPassUserId,
      sourceUrl:
        'https://kijiji.ca/v-apartments-condos/toronto/junction-loft/87654321',
      sourceSite: 'kijiji.ca',
      title: 'Hard loft in the Junction — exposed brick',
      description:
        'Authentic hard loft in a converted printing-press building. 14ft ceilings, original timber beams, exposed brick on three walls. Pet-friendly, no smoking. Walk score 96, transit score 91.',
      imageUrls: [
        'https://images.unsplash.com/photo-1505691938895-1758d7feb511?w=900',
        'https://images.unsplash.com/photo-1497366216548-37526070297c?w=900',
      ],
      priceMonthly: 2150,
      priceCurrency: 'CAD',
      bedrooms: 1,
      bathrooms: 1,
      propertyType: 'apartment',
      city: 'Toronto',
      fullAddress: '230 Mulock Ave, Toronto, ON',
      isAvailable: true,
      availableDate: null,
      lastScrapedAt: now - 9 * 60 * 60 * 1000,
      scrapeStatus: 'success',
      scrapeNotes: '',
      priceHistory: junctionLoftPriceHistory,
      availabilityHistory: junctionLoftAvailability,
      createdAt: now - 14 * ONE_DAY_MS,
      updatedAt: now - 9 * 60 * 60 * 1000,
    },
    {
      id: 'imported_seed_bloor_bungalow',
      quickPassUserId,
      sourceUrl: 'https://realtor.ca/real-estate/24681012/bloor-west-bungalow',
      sourceSite: 'realtor.ca',
      title: 'Detached bungalow with backyard — Bloor West',
      description:
        'Three-bedroom detached bungalow on a deep lot. Hardwood throughout, eat-in kitchen, finished basement with second kitchen for in-laws or rental. Two-car driveway. Steps to Bloor West Village shops and Runnymede station.',
      imageUrls: [
        'https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=900',
        'https://images.unsplash.com/photo-1572120360610-d971b9d7767c?w=900',
      ],
      priceMonthly: 2950,
      priceCurrency: 'CAD',
      bedrooms: 3,
      bathrooms: 2,
      propertyType: 'house',
      city: 'Toronto',
      fullAddress: '142 Riverview Gardens, Toronto, ON',
      isAvailable: true,
      availableDate: now + 14 * ONE_DAY_MS,
      lastScrapedAt: now - 1 * ONE_DAY_MS,
      scrapeStatus: 'success',
      scrapeNotes: '',
      priceHistory: bloorBungalowPriceHistory,
      availabilityHistory: bloorBungalowAvailability,
      createdAt: now - 3 * ONE_WEEK_MS,
      updatedAt: now - 1 * ONE_DAY_MS,
    },
    {
      id: 'imported_seed_roncesvalles_condo',
      quickPassUserId,
      sourceUrl: 'https://padmapper.com/buildings/roncesvalles-condo/9988',
      sourceSite: 'padmapper.com',
      title: 'One-bedroom condo on Roncesvalles',
      description:
        'Updated one-bedroom in a low-rise condo. New stainless appliances, quartz counters, en-suite laundry, juliet balcony with park view. Walking distance to High Park, the lake, and the 504 streetcar.',
      imageUrls: [
        'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=900',
      ],
      priceMonthly: 2300,
      priceCurrency: 'CAD',
      bedrooms: 1,
      bathrooms: 1,
      propertyType: 'condo',
      city: 'Toronto',
      fullAddress: '305 Roncesvalles Ave, Toronto, ON',
      isAvailable: false,
      availableDate: null,
      lastScrapedAt: now - 2 * ONE_DAY_MS,
      scrapeStatus: 'success',
      scrapeNotes:
        "Listing was pulled by the landlord. We'll keep checking in case it relists.",
      priceHistory: roncesvallesCondoPriceHistory,
      availabilityHistory: roncesvallesCondoAvailability,
      createdAt: now - 4 * ONE_WEEK_MS,
      updatedAt: now - 2 * ONE_DAY_MS,
    },
    {
      id: 'imported_seed_beaches_duplex',
      quickPassUserId,
      sourceUrl: 'https://zumper.com/apartments/55667788/beaches-duplex',
      sourceSite: 'zumper.com',
      title: 'Upper duplex two blocks from the lake',
      description:
        'Charming upper unit of a brick duplex two blocks from the boardwalk. Three bedrooms, eat-in kitchen, hardwood floors, shared backyard. Pets considered. Looking for tenants for a one-year minimum lease.',
      imageUrls: [],
      priceMonthly: 2750,
      priceCurrency: 'CAD',
      bedrooms: 3,
      bathrooms: 1,
      propertyType: 'house',
      city: 'Toronto',
      fullAddress: '88 Hammersmith Ave, Toronto, ON',
      isAvailable: null,
      availableDate: null,
      lastScrapedAt: now - 3 * ONE_DAY_MS,
      scrapeStatus: 'failed',
      scrapeNotes:
        "Couldn't fetch the page on the last three tries — site may have taken the listing down. Try opening it manually.",
      priceHistory: [
        {
          observedAt: now - 6 * ONE_DAY_MS,
          priceMonthly: 2750,
          priceCurrency: 'CAD',
        },
      ],
      availabilityHistory: [
        {
          observedAt: now - 6 * ONE_DAY_MS,
          isAvailable: true,
          availableDate: null,
        },
      ],
      createdAt: now - 6 * ONE_DAY_MS,
      updatedAt: now - 3 * ONE_DAY_MS,
    },
  ]
}

export { buildSeedImportedListings }
